import React from 'react';

/**
 * Componente de paginación simplificado para dispositivos móviles
 * 
 * @description Implementa el patrón "Load More" optimizado para UX móvil.
 * Muestra un botón "Ver más" que carga progresivamente más resultados,
 * evitando la navegación compleja típica de paginación desktop. El componente
 * se oculta automáticamente cuando no hay más páginas disponibles o cuando
 * los parámetros son inválidos.
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {number} props.currentPage - Página actual (0-indexed)
 * @param {number} props.total - Total de elementos disponibles (debe ser > 0)
 * @param {number} props.pageSize - Elementos por página (debe ser > 0)
 * @param {function} props.onPageChange - Callback ejecutado al solicitar siguiente página
 * @param {number} props.onPageChange.nextPage - Número de la siguiente página a cargar
 * @param {number} [props.loadedItems=0] - Número real de elementos cargados (fix iPhone)
 * 
 * @returns {JSX.Element|null} Botón "Ver más" o null si no hay más páginas disponibles
 * 
 * @example
 * // Uso básico con estado local
 * const [page, setPage] = useState(0);
 * const [data, setData] = useState({ hits: [], total: 0 });
 * 
 * <PaginationMobile
 *   currentPage={page}
 *   total={data.total}
 *   pageSize={20}
 *   onPageChange={(nextPage) => {
 *     setPage(nextPage);
 *     loadMoreResults(nextPage);
 *   }}
 * />
 * 
 * @example
 * // Integración con BuscadorApp
 * <PaginationMobile
 *   currentPage={page}
 *   total={data ? data.total : 0}
 *   pageSize={PAGESIZE}
 *   onPageChange={handlePageChangeMobile}
 * />
 * 
 * @example
 * // Casos donde no se renderiza (retorna null):
 * // - total <= 0 o pageSize <= 0
 * // - onPageChange no es función
 * // - totalPages <= 1 (solo una página)
 * // - isLastPage === true (última página alcanzada)
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ./pagination.js} Versión desktop de paginación
 * @see {@link ../BuscadorApp.js} Componente padre que utiliza este componente
 */
export default function PaginationMobile({ currentPage, total, pageSize, onPageChange, loadedItems = 0 }) {
  if (!total || !pageSize || pageSize <= 0) return null;
  if (typeof onPageChange !== 'function') return null;

  /**
   * Cálculo mejorado para detección de última página con validación triple
   * 
   * @description Implementa triple validación para detectar correctamente la última página:
   * 1. Validación por páginas: currentPage >= totalPages - 1
   * 2. Validación por elementos teóricos: itemsLoaded >= total
   * 3. Validación por elementos reales: loadedItems >= total (fix para iPhone)
   * 
   * La tercera validación usa el número real de elementos cargados en el array,
   * lo que soluciona problemas de sincronización en dispositivos iOS.
   */
  const totalPages = Math.ceil(total / pageSize);
  const itemsLoaded = (currentPage + 1) * pageSize;
  const isLastPage = currentPage >= totalPages - 1 || itemsLoaded >= total || loadedItems >= total;

  console.log(" items > total: ", itemsLoaded, " > ", total)

  if (totalPages <= 1 || isLastPage) return null; // No mostrar si no hay más páginas

  return (
    <div id="paginacion">
      <button
        type="button"
        className='showMore-button'
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isLastPage}
        aria-label="Cargar más resultados"
      >
        Ver más
      </button>
    </div>
  );
}